import React, { FC, memo, useCallback, useMemo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { generateContractPdfLink, useGetContractsByUserIdQuery } from 'entities/Contract';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import dayjs from 'dayjs';
import { Divider } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { getGlobalSettings } from 'app/appState';
import { BookingDocumentsNames } from 'app/appState/model/types';
import { contractDetailsDrawerActions } from 'widgets/ContractDetailsDrawer';
import { Button } from 'shared/ui/Button';
import { WarningPing } from 'shared/ui/WarningPing';
import { Invoice } from 'entities/Invoice';
import { formatDecimal } from 'shared/utils/helpers/formatDecimal';
import { roundNumber } from 'shared/utils/helpers/roundNumber';

interface PersonalContractsProps {
  userId: string | undefined;
  openPaymentModal: (invoice: Invoice) => void;
}

export const PersonalContracts: FC<PersonalContractsProps> = memo((props) => {
  const { userId, openPaymentModal } = props;

  const { t } = useAppTranslation(['contracts', 'common', 'booking']);

  const dispatch = useAppDispatch();

  const globalSettings = useAppSelector(getGlobalSettings);

  const boxUsageRulesDocument = useMemo(
    () => globalSettings?.bookingDocuments.find(({ docName }) => docName === BookingDocumentsNames.RULES),
    [globalSettings?.bookingDocuments],
  );

  const { data } = useGetContractsByUserIdQuery({ userId, isActive: true }, { skip: !userId });

  const openContractDetails = useCallback(
    (contractId: string): void => {
      dispatch(contractDetailsDrawerActions.setOpenContractDetails(contractId));
    },
    [dispatch],
  );
  return (
    <div className="flex flex-col gap-2">
      {data?.map((contract, index, array) => {
        const today = dayjs().startOf('day');

        const firstUnpaidInvoice = contract?.invoices
          ? [...contract.invoices]
              .sort((a, b) => dayjs(a.dueDate).diff(dayjs(b.dueDate)))
              .find(
                (invoice) =>
                  (dayjs(invoice.dueDate).startOf('day').isSame(today) || dayjs(invoice.dueDate).startOf('day').isBefore(today)) &&
                  invoice.debt > 0,
              )
          : null;

        const lastPaidInvoice = contract?.invoices
          ? [...contract.invoices]
              .sort((a, b) => dayjs(b.dueDate).diff(dayjs(a.dueDate)))
              .find((invoice) => invoice.debt === 0 && !invoice.relatedInvoiceId)
          : null;

        return (
          <div key={contract.contractId} className="flex flex-col items-start gap-2">
            <div className="text-xl flex justify-between items-center w-full">
              <div className="text-2xl flex font-semibold font-tektur items-center">
                {t('Contract')}:
                <span
                  className="pl-1 text-accent font-semibold cursor-pointer underline text-xl font-tektur"
                  onClick={() => {
                    openContractDetails(contract.contractId);
                  }}
                >
                  #{contract.contractNumber}
                </span>
              </div>

              {firstUnpaidInvoice && (
                <div className="flex justify-end items-center gap-2">
                  <span className="text-error font-medium">{t('Debt')}</span>
                  <WarningPing type="error" />
                </div>
              )}
            </div>
            {contract.contractItems
              ?.filter(({ box }) => box)
              .map(({ contractItemId, box }) => (
                <div key={contractItemId} className="text-sm text-primaryLight">
                  {t('Box')} {box?.name}:{' '}
                  {[
                    { value: box?.length, label: t('D') },
                    { value: box?.width, label: t('W') },
                    { value: box?.height, label: t('H') },
                  ]
                    .filter(({ value }) => value)
                    .map(({ value, label }) => `${formatDecimal(roundNumber(value))} м (${label})`)
                    .join(' × ')}
                </div>
              ))}
            {boxUsageRulesDocument && (
              <Button
                theme="clear"
                fontSize="small"
                size="inline"
                className="underline !w-fit"
                onClick={() => {
                  window.open(boxUsageRulesDocument.link, '_blank', 'noopener,noreferrer');
                }}
              >
                {t(boxUsageRulesDocument.docName, { ns: 'booking' })}
              </Button>
            )}
            {(firstUnpaidInvoice || lastPaidInvoice) && (
              <div className="flex justify-between w-full">
                <div className="flex gap-2 items-center">
                  <div
                    className={`text-sm rounded-xl px-4 py-0.5 ${
                      firstUnpaidInvoice ? 'text-error bg-errorLight' : 'text-success bg-successLight'
                    }`}
                  >
                    {t(firstUnpaidInvoice ? 'Not paid' : 'Paid')}
                  </div>
                  <div className="text-primaryLight">
                    {firstUnpaidInvoice
                      ? `${t('from')} ${getClientDateFormat(firstUnpaidInvoice.dueDate)}`
                      : lastPaidInvoice
                      ? `${t('to')} ${getClientDateFormat(lastPaidInvoice?.dateTo)}`
                      : ''}
                  </div>
                </div>
                {firstUnpaidInvoice && (
                  <Button
                    theme="danger"
                    size="small"
                    onClick={() => {
                      openPaymentModal(firstUnpaidInvoice);
                    }}
                  >
                    {t('Pay')}
                  </Button>
                )}
              </div>
            )}

            <div className="flex gap-2 mt-2 w-1/2">
              <Button
                theme="dark"
                containerClassName="w-full"
                size="small"
                onClick={() => {
                  openContractDetails(contract.contractId);
                }}
              >
                {t('Details')}
              </Button>
              <a href={generateContractPdfLink(contract.contractId)} target="_blank" rel="noopener noreferrer" className="w-full">
                <Button theme="outlined" containerClassName="w-full" size="small">
                  {t('Download PDF')}
                </Button>
              </a>
            </div>
            {index < array.length - 1 ? <Divider className="border-secondaryAccent my-2" /> : null}
          </div>
        );
      })}
    </div>
  );
});
